"use client";

import { createT } from "@/app/[lang]/_utils/create-t";
import type { Lang } from "@/langs";
import { api } from "@/trpc/react";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@borderless/ui/components/input-group";
import { Spinner } from "@borderless/ui/components/spinner";
import { SearchIcon } from "lucide-react";
import type { Route } from "next";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import type { FC } from "react";
import { useDebounceCallback } from "usehooks-ts";
import { StoryInList } from "../StoryInList";
import dictionary from "./t.json";

const t = createT(dictionary);

export const Search: FC<{
  lang: Lang;
}> = (props) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const searchTerm = searchParams.get("q");
  const { data, isFetching, error, refetch } = api.post.search.useQuery(
    {
      lang: props.lang,
      searchTerm: searchTerm ?? "",
    },
    {
      enabled: searchTerm !== null && searchTerm.length > 0,
      refetchOnWindowFocus: false,
    },
  );
  const debouncedSearch = useDebounceCallback((searchTerm) => {
    if (typeof searchTerm === "string") {
      const params = new URLSearchParams(searchParams);
      if (searchTerm.length > 0) {
        params.set("q", searchTerm);
      } else {
        params.delete("q");
      }
      router.push(`${pathname}?${params.toString()}` as Route);
    }
  }, 500);
  return (
    <div className="mx-auto max-w-3xl">
      <div className="mx-auto max-w-md">
        <InputGroup>
          <InputGroupInput
            type="text"
            onChange={(e) => debouncedSearch(e.target.value)}
            defaultValue={searchTerm ?? ""}
            placeholder={t("Search for stories...", props.lang, true)}
          />
          <InputGroupAddon align="inline-end">
            <InputGroupButton
              onClick={(e) => {
                e.preventDefault();
                void refetch(); // this is not needed since the search is done on input change, but just in case.
              }}
              disabled={
                isFetching || !(searchTerm !== null && searchTerm.length > 0)
              }
              size="icon-sm"
            >
              {isFetching ? (
                <Spinner />
              ) : (
                <SearchIcon className="text-bright-green" />
              )}
            </InputGroupButton>
          </InputGroupAddon>
        </InputGroup>
        {error && (
          <span className="text-xs text-destructive">{error.message}</span>
        )}
      </div>
      <div className="h-6 sm:h-12" />
      {searchTerm !== null && searchTerm.length > 0 && (
        <ul className="space-y-4">
          {data?.map((post) => (
            <li key={post.slug}>
              <StoryInList post={post} lang={props.lang} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
